import userService from "./UserService";

// The user's location is resolved entirely by MLI's geolocation API, keyed by
// patientId. There is no device-based path: the app never reads
// `navigator.geolocation` or Capacitor Geolocation, so there is no permission
// prompt and no GPS dependency. A failure here is not fatal — every caller
// falls back to letting the user pick on the map.
const DEFAULT_TIMEOUT_MS = 15000;

export class LocationError extends Error {
  constructor(code, message, cause) {
    super(message);
    this.name = "LocationError";
    this.code = code;
    // Keep the underlying failure inspectable: a 404 on a wrong path, a 500 and
    // a genuine "nothing on record" all surface the same message to the user.
    if (cause !== undefined) this.cause = cause;
  }
}

// Resolves to { lat, lng, accuracy, source }. `accuracy` is always null — the
// API returns a stored location, not a measured fix, so there is no radius to
// report. Rejects with a LocationError (UNAVAILABLE | TIMEOUT).
export async function getCurrentLocation({
  patientId,
  timeoutMs = DEFAULT_TIMEOUT_MS,
} = {}) {
  if (patientId == null || patientId === "") {
    throw new LocationError("UNAVAILABLE", "No patientId to look up location");
  }

  const startedAt = Date.now();
  let res;
  try {
    res = await userService.getGeolocation(patientId, { timeout: timeoutMs });
  } catch (err) {
    // The shared axios response interceptor reads `err.response.status`
    // unguarded, so every response-less failure (timeout, offline, CORS) has
    // already been turned into a TypeError by the time it reaches us — its
    // `code` and `response` are gone. Elapsed time is what survives: only a
    // real timeout burns the whole budget, while the rest fail fast.
    if (err?.code === "ECONNABORTED" || Date.now() - startedAt >= timeoutMs) {
      throw new LocationError("TIMEOUT", "Location lookup timed out", err);
    }
    throw new LocationError("UNAVAILABLE", "Location lookup failed", err);
  }

  const { latitude, longitude } = res?.data || {};
  // 0,0 is this app's "no coordinates recorded" sentinel (see the isMissingCoords
  // helpers in selectLocation.js and manageAddress.js), not a real position, and
  // Number.isFinite(0) is true — so it has to be rejected explicitly.
  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    (latitude === 0 && longitude === 0)
  ) {
    throw new LocationError("UNAVAILABLE", "No location on record");
  }

  return {
    lat: latitude,
    lng: longitude,
    accuracy: null,
    source: "mli-api",
  };
}

export function userMessageForLocationError(err) {
  if (!(err instanceof LocationError)) {
    return "Couldn't fetch your location. Please pick on the map instead.";
  }
  switch (err.code) {
    case "TIMEOUT":
      return "Locating took too long. Please try again or pick on the map.";
    case "UNAVAILABLE":
    default:
      return "Your location is unavailable right now. Please pick on the map.";
  }
}
